// Domo Cero — Pilares derivados del Índice de Silicio.
//
// El bridge real con `scan_vibracional` (los valores reales de los 6
// pilares por Tripulante) requiere un RPC nuevo en Supabase y
// extensión del PlayerSnapshot. Mientras esa pieza no esté lista,
// derivamos los 6 pilares CLIENT-SIDE de forma determinista a partir
// del Índice de Silicio + el id del peer (clerk_user_id), de modo que
// dos Tripulantes con el mismo Índice pero distinta identidad muestran
// distribuciones distintas pero estables a través del tiempo (no
// flickerean entre cada apertura del panel).
//
// Promedio de los 6 valores ≈ siliconIndex (con dispersión ±10 según
// el seed). Cada pilar nunca cae bajo 5 ni sube de 99 — incluso en
// extremos del Índice se preserva la matemática del Escáner.
//
// Cuando el bridge real (RPC `get_player_pillar_scores_by_clerk_id`)
// se materialice, este archivo se queda como fallback para Tripulantes
// sin scan previo.

export interface PillarScores {
    fisico: number
    mental: number
    emocional: number
    financiero: number
    vector: number
    orbita: number
}

const PILLAR_KEYS: (keyof PillarScores)[] = [
    "fisico",
    "mental",
    "emocional",
    "financiero",
    "vector",
    "orbita",
]

// PRNG estable seedeado por string — pequeño Mulberry32 sobre hash xfnv1a.
function hashString(s: string): number {
    let h = 2166136261 >>> 0
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i)
        h = Math.imul(h, 16777619)
    }
    return h >>> 0
}

function mulberry32(seed: number) {
    let s = seed >>> 0
    return () => {
        s = (s + 0x6d2b79f5) >>> 0
        let t = s
        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

export function derivePillarScores(
    clerkUserId: string,
    siliconIndex: number
): PillarScores {
    const idx = Math.max(0, Math.min(100, siliconIndex))
    const seed = hashString(clerkUserId || "anon") ^ Math.round(idx * 1000)
    const rand = mulberry32(seed)

    // Generamos 6 dispersiones centradas en cero (rango ±20).
    const raw: number[] = []
    let sum = 0
    for (let i = 0; i < 6; i++) {
        // Dos pasadas de uniform → distribución cuasi-normal.
        const a = rand()
        const b = rand()
        const dispersion = (a + b - 1) * 20 // -20..+20
        raw.push(dispersion)
        sum += dispersion
    }
    // Normalizar para que el promedio sea exactamente idx (preserva
    // la matemática del Índice de Silicio = avg de pilares).
    const mean = sum / 6
    const out: PillarScores = {} as PillarScores
    PILLAR_KEYS.forEach((k, i) => {
        const v = idx + (raw[i] - mean)
        out[k] = Math.max(5, Math.min(99, Math.round(v)))
    })
    return out
}

export const PILLAR_LABELS: Record<keyof PillarScores, string> = {
    fisico: "Hardware",
    mental: "Procesador",
    emocional: "Motor",
    financiero: "Gravedad",
    vector: "Vector",
    orbita: "Órbita",
}

export const PILLAR_SUBTITLES: Record<keyof PillarScores, string> = {
    fisico: "Físico",
    mental: "Mental",
    emocional: "Emocional",
    financiero: "Financiera",
    vector: "De Expansión",
    orbita: "Relacional",
}
